import {
  bookingPolicyOptions,
  costLevelLabels,
  createDefaultFilterState,
  dimensionDefinitions,
  mobilityLimitOptions,
  neighbourhoodCharacterDefinitions,
  seasonDefinitions,
  timeOfDayDefinitions,
  transportModeDefinitions,
  travellerDefinitions,
  waterRelationshipDefinitions,
} from "./filter-config.js";
import type {
  BookingPolicy,
  CostLevel,
  DimensionKey,
  FilterChip,
  FilterState,
  MatchReason,
  Mobility,
  MobilityLimit,
  NeighbourhoodCharacter,
  RankedAttraction,
  RelaxSuggestion,
  Season,
  SortOption,
  TimeOfDay,
  TransportMode,
  TravelContentEntry,
  TravellerType,
  WaterRelationship,
} from "./types.js";

const costRanks: Record<CostLevel, number> = {
  free: 0,
  budget: 1,
  moderate: 2,
  expensive: 3,
  premium: 4,
};

const mobilityRanks: Record<Mobility, number> = {
  easy: 0,
  moderate: 1,
  challenging: 2,
  limited_accessibility: 3,
};

const mobilityLimitRanks: Record<Exclude<MobilityLimit, "any">, number> = {
  easy: 0,
  moderate: 1,
  challenging: 2,
};

const dimensionLabels = new Map(
  dimensionDefinitions.map((definition) => [definition.key, definition.shortLabel]),
);
const travellerLabels = new Map(
  travellerDefinitions.map((definition) => [definition.value, definition.label]),
);
const seasonLabels = new Map(seasonDefinitions.map((definition) => [definition.value, definition.label]));
const timeLabels = new Map(timeOfDayDefinitions.map((definition) => [definition.value, definition.label]));
const transportLabels = new Map(
  transportModeDefinitions.map((definition) => [definition.value, definition.label]),
);
const characterLabels = new Map(
  neighbourhoodCharacterDefinitions.map((definition) => [definition.value, definition.label]),
);
const waterLabels = new Map(
  waterRelationshipDefinitions.map((definition) => [definition.value, definition.label]),
);
const bookingLabels = new Map(bookingPolicyOptions.map((definition) => [definition.value, definition.label]));
const mobilityLabels = new Map(mobilityLimitOptions.map((definition) => [definition.value, definition.label]));

export function getDimensionScore(attraction: TravelContentEntry, key: DimensionKey): number {
  const profile = attraction.experienceProfile;

  switch (key) {
    case "sunny":
      return profile.weather?.sunnySuitability ?? attraction.ratings.sunny?.score ?? attraction.ratings.summer?.score ?? 0;
    case "rain":
      return profile.weather?.rainSuitability ?? attraction.ratings.rain?.score ?? 0;
    case "cold":
      return profile.weather?.coldSuitability ?? attraction.ratings.winter?.score ?? 0;
    case "history":
      return profile.history?.score ?? 0;
    case "culture":
      return profile.culture?.score ?? 0;
    case "atmosphere":
      return profile.atmosphere?.score ?? 0;
    case "walking":
      return profile.walking?.walkability ?? profile.walking?.score ?? 0;
    case "water":
      return profile.water?.score ?? 0;
    case "localness":
      return profile.localness?.score ?? 0;
    case "food":
      return profile.food?.score ?? 0;
    case "photography":
      return profile.photography?.score ?? 0;
    case "children":
      return attraction.ratings.children?.score ?? 0;
    case "value":
      return attraction.ratings.valueForMoney?.score ?? attraction.cost?.valueForMoneyScore?.score ?? 0;
    case "spontaneity":
      return profile.spontaneity?.score ?? 0;
    case "experienceDensity":
      return profile.experienceDensity?.score ?? profile.neighbourhoods?.experienceDensity ?? 0;
    case "accessibility":
      return attraction.ratings.accessibility?.score ?? 0;
    case "nature":
      return profile.nature?.score ?? 0;
    case "romance":
      return profile.romance?.score ?? 0;
    case "architecture":
      return profile.architecture?.score ?? profile.photography?.architecture ?? 0;
    case "timeEfficiency":
      return profile.timeEfficiency?.score ?? profile.timeEfficiency?.experiencePerHour ?? 0;
  }
}

export function getDimensionReason(
  attraction: TravelContentEntry,
  key: DimensionKey,
): string | undefined {
  const profile = attraction.experienceProfile;

  switch (key) {
    case "sunny":
      return attraction.ratings.sunny?.reason
        ?? (profile.weather?.sunnySuitability === 5
          ? "Spielt seine Stärken bei trockenem, freundlichem Wetter besonders aus."
          : undefined);
    case "rain": {
      const explicitReason = attraction.ratings.rain?.reason;
      if (explicitReason) return explicitReason;
      return profile.weather?.weatherDependent === false
        ? "Funktioniert weitgehend unabhängig vom Wetter."
        : undefined;
    }
    case "cold":
      return attraction.ratings.winter?.reason;
    case "history":
      return profile.history?.reason;
    case "culture":
      return profile.culture?.reason;
    case "atmosphere":
      return profile.atmosphere?.reason;
    case "walking":
      return profile.walking?.bestFor?.[0];
    case "water":
      return profile.water?.relationship?.length
        ? `Wasserbezug: ${profile.water.relationship.map(formatWaterRelationship).join(", ")}.`
        : undefined;
    case "localness":
      return profile.localness?.reason;
    case "food":
      return profile.food?.foodStopsPossible
        ? "Lässt sich gut mit Fika oder einer Essenspause verbinden."
        : undefined;
    case "photography":
      return profile.photography?.viewpoints?.[0];
    case "children":
      return attraction.ratings.children?.reason;
    case "value":
      return attraction.ratings.valueForMoney?.reason ?? attraction.cost?.valueForMoneyScore?.reason;
    case "spontaneity":
      return profile.spontaneity?.dropInFriendly
        ? "Eignet sich gut für eine kurzfristige Entscheidung."
        : undefined;
    case "experienceDensity":
      return profile.experienceDensity?.reason;
    case "accessibility":
      return attraction.ratings.accessibility?.reason;
    case "nature":
      return profile.nature?.reason;
    case "romance":
      return profile.romance?.reason;
    case "architecture":
      return profile.architecture?.reason;
    case "timeEfficiency":
      return profile.timeEfficiency?.score
        ? "Gutes Verhältnis aus Anfahrt, Dauer und Erlebniswert."
        : undefined;
  }
}

export function formatWaterRelationship(value: WaterRelationship): string {
  return waterLabels.get(value) ?? value;
}

function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("de")
    .trim();
}

function searchableText(attraction: TravelContentEntry): string {
  const profile = attraction.experienceProfile;
  const sunnySearchTerms = getDimensionScore(attraction, "sunny") >= 4
    ? ["Sonne", "sonnig", "gutes Wetter", attraction.ratings.sunny?.label, attraction.ratings.sunny?.reason]
    : [];
  const dimensionTags = [
    profile.atmosphere,
    profile.localness,
    profile.uniqueness,
    profile.wowFactor,
    profile.romance,
    profile.architecture,
    profile.history,
    profile.nature,
    profile.culture,
    profile.nightlife,
    profile.shopping,
    profile.wellness,
    profile.adventure,
    profile.experienceDensity,
  ].flatMap((dimension) => dimension?.tags ?? []);

  return normalize(
    [
      attraction.title,
      attraction.image?.label,
      attraction.destination.city,
      attraction.destination.neighbourhood,
      ...sunnySearchTerms,
      attraction.editorial?.headline,
      attraction.editorial?.dek,
      attraction.editorial?.summary,
      attraction.editorial?.decision,
      attraction.editorial?.honestVerdict,
      ...(attraction.editorial?.bestFor ?? []),
      ...(attraction.editorial?.notIdealFor ?? []),
      ...(attraction.intent.searchThemes ?? []),
      ...(attraction.intent.userQuestions ?? []),
      ...(profile.neighbourhoods?.areas ?? []),
      ...(profile.food?.foodTypes ?? []),
      ...(profile.walking?.bestFor ?? []),
      ...(profile.photography?.viewpoints ?? []),
      ...dimensionTags,
      ...(attraction.content.sections ?? []).flatMap((section) => [section.heading, section.body]),
      ...(attraction.content.faq ?? []).flatMap((item) => [item.question, item.answer]),
    ]
      .filter((value): value is string => Boolean(value))
      .join(" "),
  );
}

function includesTravellerType(attraction: TravelContentEntry, travellerType: TravellerType): boolean {
  return attraction.audience.travellerTypes?.includes(travellerType) ?? false;
}

export function getTravellerScore(attraction: TravelContentEntry, travellerType: TravellerType): number {
  const explicitMatch = includesTravellerType(attraction, travellerType) ? 5 : 0;
  const profile = attraction.experienceProfile;
  const contextualScore = (() => {
    switch (travellerType) {
      case "first_time":
        return attraction.ratings.firstTimeVisitors?.score ?? 0;
      case "repeat_visitor":
        return attraction.ratings.repeatVisitors?.score ?? 0;
      case "family":
        return attraction.ratings.children?.score ?? 0;
      case "couple":
        return attraction.ratings.couples?.score ?? 0;
      case "solo":
        return attraction.ratings.soloTravellers?.score ?? 0;
      case "history":
        return profile.history?.score ?? 0;
      case "culture":
        return profile.culture?.score ?? 0;
      case "photography":
        return profile.photography?.score ?? 0;
      case "foodie":
        return profile.food?.score ?? 0;
      case "nature":
        return profile.nature?.score ?? 0;
      case "design":
        return profile.architecture?.score ?? 0;
      case "budget":
        return attraction.ratings.valueForMoney?.score ?? 0;
      case "luxury":
        return attraction.cost?.costLevel === "premium" ? 5 : explicitMatch;
      case "nightlife":
        return profile.nightlife?.score ?? 0;
      case "wellness":
        return profile.wellness?.score ?? 0;
      case "adventure":
        return profile.adventure?.score ?? 0;
      case "senior":
        return explicitMatch || attraction.ratings.accessibility?.score || 0;
      case "friends":
        return explicitMatch;
    }
  })();

  return Math.max(explicitMatch, contextualScore);
}

export function isNoBooking(attraction: TravelContentEntry): boolean {
  return (
    attraction.logistics?.advanceBooking === "never" &&
    attraction.cost?.bookingRequired !== true &&
    attraction.experienceProfile.spontaneity?.reservationRequired !== true
  );
}

function isBookingNotEssential(attraction: TravelContentEntry): boolean {
  return (
    attraction.logistics?.advanceBooking !== "essential" &&
    attraction.cost?.bookingRequired !== true &&
    attraction.experienceProfile.spontaneity?.reservationRequired !== true
  );
}

export function getComparablePrice(attraction: TravelContentEntry): number | undefined {
  if (attraction.cost?.costLevel === "free") return 0;
  return attraction.cost?.adultPrice ?? attraction.cost?.priceFrom ?? attraction.cost?.estimatedTotalCost;
}

function intersects<T>(selected: T[], available: T[] | undefined): boolean {
  if (selected.length === 0) return true;
  if (!available?.length) return false;
  return selected.some((value) => available.includes(value));
}

function childAgeMatches(attraction: TravelContentEntry, age: number): boolean {
  const children = attraction.audience.children;
  if (!children || children.suitable !== true) return false;
  if (children.minAge !== undefined && age < children.minAge) return false;
  if (children.maxAge !== undefined && age > children.maxAge) return false;
  return true;
}

function passesFilters(attraction: TravelContentEntry, state: FilterState): boolean {
  const queryTerms = normalize(state.query).split(/\s+/).filter(Boolean);
  const haystack = searchableText(attraction);
  if (queryTerms.some((term) => !haystack.includes(term))) return false;

  for (const [key, minimum] of Object.entries(state.minScores) as Array<[DimensionKey, number]>) {
    if (minimum > 0 && getDimensionScore(attraction, key) < minimum) return false;
  }

  if (state.travellerTypes.some((travellerType) => getTravellerScore(attraction, travellerType) < 4)) {
    return false;
  }

  if (state.maxCostLevel !== "any") {
    const attractionCost = attraction.cost?.costLevel;
    if (!attractionCost || costRanks[attractionCost] > costRanks[state.maxCostLevel]) return false;
  }

  if (state.maxPriceSek !== null) {
    const price = getComparablePrice(attraction);
    if (price === undefined || price > state.maxPriceSek) return false;
  }

  if (state.maxDurationMinutes !== null) {
    const duration = attraction.time?.recommendedDurationMinutes ?? attraction.time?.typicalDurationMinutes;
    if (duration === undefined || duration > state.maxDurationMinutes) return false;
  }

  if (state.maxTravelTimeMinutes !== null) {
    const travelTime = attraction.time?.travelTimeFromCentralStationMinutes;
    if (travelTime === undefined || travelTime > state.maxTravelTimeMinutes) return false;
  }

  if (state.minIndoorPercentage > 0) {
    const indoorPercentage = attraction.weather?.indoorPercentage;
    if (indoorPercentage === undefined || indoorPercentage < state.minIndoorPercentage) return false;
  }

  if (!intersects(state.seasons, attraction.tripContext?.season)) return false;
  if (!intersects(state.timesOfDay, attraction.time?.bestTimeOfDay)) return false;

  if (state.maxMobility !== "any") {
    const mobility = attraction.audience.mobility;
    if (!mobility || mobilityRanks[mobility] > mobilityLimitRanks[state.maxMobility]) return false;
  }

  if (state.bookingPolicy === "no_booking" && !isNoBooking(attraction)) return false;
  if (state.bookingPolicy === "not_essential" && !isBookingNotEssential(attraction)) return false;

  if (!intersects(state.transportModes, attraction.experienceProfile.transport?.modes)) return false;
  if (!intersects(state.neighbourhoodCharacters, attraction.experienceProfile.neighbourhoods?.neighbourhoodCharacter)) return false;
  if (!intersects(state.waterRelationships, attraction.experienceProfile.water?.relationship)) return false;

  if (state.rainSafeOnly && attraction.weather?.rainSafe !== true) return false;
  if (state.childrenOnly && attraction.audience.children?.suitable !== true) return false;
  if (state.childAge !== null && !childAgeMatches(attraction, state.childAge)) return false;

  if (state.carFreeOnly) {
    const logisticsSignal = attraction.logistics?.carRequired;
    const profileSignal = attraction.experienceProfile.transport?.carNeeded;
    const explicitlyCarFree = logisticsSignal === false || profileSignal === false;
    if (!explicitlyCarFree || logisticsSignal === true || profileSignal === true) return false;
  }

  if (state.quietOnly && attraction.experienceProfile.crowds?.quietTimeAvailable !== true) return false;
  if (state.toiletsOnly && attraction.logistics?.toiletAvailability !== true) return false;

  if (state.minStrollerScore > 0) {
    const score = attraction.logistics?.strollerFriendly?.score;
    if (score === undefined || score < state.minStrollerScore) return false;
  }

  if (state.maxPhysicalEffort !== null) {
    const effort = attraction.experienceProfile.effort?.physicalEffort;
    if (effort === undefined || effort > state.maxPhysicalEffort) return false;
  }

  return true;
}

function calculateMatchScore(attraction: TravelContentEntry, state: FilterState): number {
  let weightedScore = 0;
  let totalWeight = 0;

  for (const [key, minimum] of Object.entries(state.minScores) as Array<[DimensionKey, number]>) {
    if (minimum === 0) continue;
    const dimensionWeight = key === "sunny" ? 4 : 3;
    weightedScore += (getDimensionScore(attraction, key) / 5) * 100 * dimensionWeight;
    totalWeight += dimensionWeight;
  }

  for (const travellerType of state.travellerTypes) {
    weightedScore += (getTravellerScore(attraction, travellerType) / 5) * 100 * 2;
    totalWeight += 2;
  }

  const practicalFilters = [
    state.maxCostLevel !== "any",
    state.maxPriceSek !== null,
    state.maxDurationMinutes !== null,
    state.maxTravelTimeMinutes !== null,
    state.minIndoorPercentage > 0,
    state.seasons.length > 0,
    state.timesOfDay.length > 0,
    state.maxMobility !== "any",
    state.bookingPolicy !== "any",
    state.transportModes.length > 0,
    state.neighbourhoodCharacters.length > 0,
    state.waterRelationships.length > 0,
    state.rainSafeOnly,
    state.childrenOnly,
    state.childAge !== null,
    state.carFreeOnly,
    state.quietOnly,
    state.toiletsOnly,
    state.minStrollerScore > 0,
    state.maxPhysicalEffort !== null,
  ];

  for (const active of practicalFilters) {
    if (active) {
      weightedScore += 100 * 0.75;
      totalWeight += 0.75;
    }
  }

  if (state.query.trim()) {
    weightedScore += 100 * 0.5;
    totalWeight += 0.5;
  }

  const overall = attraction.ratings.overall?.score ?? 3;
  const value = attraction.ratings.valueForMoney?.score ?? 3;
  const density = getDimensionScore(attraction, "experienceDensity") || 3;
  const efficiency = getDimensionScore(attraction, "timeEfficiency") || 3;

  weightedScore += (overall / 5) * 100 * 1.5;
  totalWeight += 1.5;

  if (totalWeight <= 1.5) {
    weightedScore += (value / 5) * 100 + (density / 5) * 100 + (efficiency / 5) * 100;
    totalWeight += 3;
  }

  return Math.max(1, Math.min(100, Math.round(weightedScore / totalWeight)));
}

function getMatchReasons(attraction: TravelContentEntry, state: FilterState): MatchReason[] {
  const reasons: MatchReason[] = [];

  const activeDimensions = (Object.entries(state.minScores) as Array<[DimensionKey, number]>)
    .filter(([, minimum]) => minimum > 0)
    .map(([key]) => ({
      key,
      score: getDimensionScore(attraction, key),
      label: dimensionLabels.get(key) ?? key,
      explanation: getDimensionReason(attraction, key),
    }))
    .sort((left, right) => right.score - left.score);

  reasons.push(...activeDimensions.slice(0, 3));

  for (const travellerType of state.travellerTypes) {
    if (reasons.length >= 4) break;
    reasons.push({
      key: `traveller:${travellerType}`,
      label: travellerLabels.get(travellerType) ?? travellerType,
      score: getTravellerScore(attraction, travellerType),
    });
  }

  const practicalReasons: Array<MatchReason | false> = [
    state.rainSafeOnly && { key: "rain-safe", label: "Regensicher" },
    state.bookingPolicy === "no_booking" && { key: "booking", label: "Ohne Vorbuchung" },
    state.maxPriceSek !== null && {
      key: "price",
      label: `${getComparablePrice(attraction) ?? 0} SEK`,
    },
    state.childrenOnly && {
      key: "children",
      label: "Familientauglich",
      score: getDimensionScore(attraction, "children"),
    },
    state.quietOnly && { key: "quiet", label: "Ruhigere Zeiten möglich" },
    state.carFreeOnly && { key: "car-free", label: "Ohne Auto" },
  ];

  for (const reason of practicalReasons) {
    if (reason && reasons.length < 4) reasons.push(reason);
  }

  if (reasons.length === 0) {
    const strengths = dimensionDefinitions
      .map((definition) => ({
        key: definition.key,
        label: definition.shortLabel,
        score: getDimensionScore(attraction, definition.key),
        explanation: getDimensionReason(attraction, definition.key),
      }))
      .filter((reason) => reason.score > 0)
      .sort((left, right) => right.score - left.score)
      .slice(0, 3);
    reasons.push(...strengths);
  }

  return reasons;
}

export function filterAndRankAttractions(
  attractions: TravelContentEntry[],
  state: FilterState,
): RankedAttraction[] {
  const ranked = attractions
    .filter((attraction) => passesFilters(attraction, state))
    .map((attraction) => ({
      attraction,
      matchScore: calculateMatchScore(attraction, state),
      reasons: getMatchReasons(attraction, state),
    }));

  return ranked.sort((left, right) => {
    const leftOverall = left.attraction.ratings.overall?.score ?? 0;
    const rightOverall = right.attraction.ratings.overall?.score ?? 0;
    const leftValue = left.attraction.ratings.valueForMoney?.score ?? 0;
    const rightValue = right.attraction.ratings.valueForMoney?.score ?? 0;
    const leftDuration = left.attraction.time?.recommendedDurationMinutes ?? Number.POSITIVE_INFINITY;
    const rightDuration = right.attraction.time?.recommendedDurationMinutes ?? Number.POSITIVE_INFINITY;
    const leftPrice = getComparablePrice(left.attraction) ?? Number.POSITIVE_INFINITY;
    const rightPrice = getComparablePrice(right.attraction) ?? Number.POSITIVE_INFINITY;
    const leftTravel = left.attraction.time?.travelTimeFromCentralStationMinutes ?? Number.POSITIVE_INFINITY;
    const rightTravel = right.attraction.time?.travelTimeFromCentralStationMinutes ?? Number.POSITIVE_INFINITY;

    switch (state.sortBy) {
      case "overall":
        return rightOverall - leftOverall || right.matchScore - left.matchScore;
      case "value":
        return rightValue - leftValue || right.matchScore - left.matchScore;
      case "duration":
        return leftDuration - rightDuration || right.matchScore - left.matchScore;
      case "price":
        return leftPrice - rightPrice || right.matchScore - left.matchScore;
      case "travel":
        return leftTravel - rightTravel || right.matchScore - left.matchScore;
      case "match":
        return right.matchScore - left.matchScore || rightOverall - leftOverall;
    }
  });
}

export function getActiveFilterChips(state: FilterState): FilterChip[] {
  const chips: FilterChip[] = [];

  if (state.query.trim()) chips.push({ id: "query", label: `Suche: „${state.query.trim()}“` });

  for (const [key, minimum] of Object.entries(state.minScores) as Array<[DimensionKey, number]>) {
    if (minimum > 0) {
      chips.push({ id: `score:${key}`, label: `${dimensionLabels.get(key) ?? key} ab ${minimum}/5` });
    }
  }

  for (const travellerType of state.travellerTypes) {
    chips.push({
      id: `traveller:${travellerType}`,
      label: travellerLabels.get(travellerType) ?? travellerType,
    });
  }

  if (state.maxCostLevel !== "any") chips.push({ id: "cost", label: costLevelLabels[state.maxCostLevel] });
  if (state.maxPriceSek !== null) chips.push({ id: "price", label: `Max. ${state.maxPriceSek} SEK` });
  if (state.maxDurationMinutes !== null) chips.push({ id: "duration", label: `Max. ${formatMinutes(state.maxDurationMinutes)}` });
  if (state.maxTravelTimeMinutes !== null) chips.push({ id: "travel", label: `Anfahrt max. ${state.maxTravelTimeMinutes} Min.` });
  if (state.minIndoorPercentage > 0) chips.push({ id: "indoor", label: `Mind. ${state.minIndoorPercentage}% drinnen` });

  for (const value of state.seasons) chips.push({ id: `season:${value}`, label: seasonLabels.get(value) ?? value });
  for (const value of state.timesOfDay) chips.push({ id: `time:${value}`, label: timeLabels.get(value) ?? value });
  if (state.maxMobility !== "any") chips.push({ id: "mobility", label: mobilityLabels.get(state.maxMobility) ?? state.maxMobility });
  if (state.bookingPolicy !== "any") chips.push({ id: "booking", label: bookingLabels.get(state.bookingPolicy) ?? state.bookingPolicy });

  for (const value of state.transportModes) chips.push({ id: `transport:${value}`, label: transportLabels.get(value) ?? value });
  for (const value of state.neighbourhoodCharacters) chips.push({ id: `character:${value}`, label: characterLabels.get(value) ?? value });
  for (const value of state.waterRelationships) chips.push({ id: `water:${value}`, label: waterLabels.get(value) ?? value });

  if (state.rainSafeOnly) chips.push({ id: "rain-safe", label: "Explizit regensicher" });
  if (state.childrenOnly) chips.push({ id: "children", label: "Für Kinder geeignet" });
  if (state.childAge !== null) chips.push({ id: "child-age", label: `Kind: ${state.childAge} Jahre` });
  if (state.carFreeOnly) chips.push({ id: "car-free", label: "Ohne Auto" });
  if (state.quietOnly) chips.push({ id: "quiet", label: "Ruhigere Zeiten möglich" });
  if (state.toiletsOnly) chips.push({ id: "toilets", label: "Toilette vorhanden" });
  if (state.minStrollerScore > 0) chips.push({ id: "stroller", label: `Kinderwagen ab ${state.minStrollerScore}/5` });
  if (state.maxPhysicalEffort !== null) chips.push({ id: "effort", label: `Aufwand max. ${state.maxPhysicalEffort}/5` });

  return chips;
}

export function removeFilter(state: FilterState, chipId: string): FilterState {
  const next = structuredClone(state);
  next.activePreset = null;

  if (chipId === "query") next.query = "";
  if (chipId === "cost") next.maxCostLevel = "any";
  if (chipId === "price") next.maxPriceSek = null;
  if (chipId === "duration") next.maxDurationMinutes = null;
  if (chipId === "travel") next.maxTravelTimeMinutes = null;
  if (chipId === "indoor") next.minIndoorPercentage = 0;
  if (chipId === "mobility") next.maxMobility = "any";
  if (chipId === "booking") next.bookingPolicy = "any";
  if (chipId === "rain-safe") next.rainSafeOnly = false;
  if (chipId === "children") next.childrenOnly = false;
  if (chipId === "child-age") next.childAge = null;
  if (chipId === "car-free") next.carFreeOnly = false;
  if (chipId === "quiet") next.quietOnly = false;
  if (chipId === "toilets") next.toiletsOnly = false;
  if (chipId === "stroller") next.minStrollerScore = 0;
  if (chipId === "effort") next.maxPhysicalEffort = null;

  if (chipId.startsWith("score:")) {
    const key = chipId.replace("score:", "") as DimensionKey;
    next.minScores[key] = 0;
  }
  if (chipId.startsWith("traveller:")) {
    const value = chipId.replace("traveller:", "") as TravellerType;
    next.travellerTypes = next.travellerTypes.filter((candidate) => candidate !== value);
  }
  if (chipId.startsWith("season:")) {
    const value = chipId.replace("season:", "") as Season;
    next.seasons = next.seasons.filter((candidate) => candidate !== value);
  }
  if (chipId.startsWith("time:")) {
    const value = chipId.replace("time:", "") as TimeOfDay;
    next.timesOfDay = next.timesOfDay.filter((candidate) => candidate !== value);
  }
  if (chipId.startsWith("transport:")) {
    const value = chipId.replace("transport:", "") as TransportMode;
    next.transportModes = next.transportModes.filter((candidate) => candidate !== value);
  }
  if (chipId.startsWith("character:")) {
    const value = chipId.replace("character:", "") as NeighbourhoodCharacter;
    next.neighbourhoodCharacters = next.neighbourhoodCharacters.filter((candidate) => candidate !== value);
  }
  if (chipId.startsWith("water:")) {
    const value = chipId.replace("water:", "") as WaterRelationship;
    next.waterRelationships = next.waterRelationships.filter((candidate) => candidate !== value);
  }

  return next;
}

export function getRelaxSuggestions(
  attractions: TravelContentEntry[],
  state: FilterState,
): RelaxSuggestion[] {
  if (filterAndRankAttractions(attractions, state).length > 0) return [];

  return getActiveFilterChips(state)
    .map((chip) => {
      const relaxedState = removeFilter(state, chip.id);
      return {
        chipId: chip.id,
        label: chip.label,
        resultCount: filterAndRankAttractions(attractions, relaxedState).length,
      };
    })
    .filter((suggestion) => suggestion.resultCount > 0)
    .sort((left, right) => right.resultCount - left.resultCount)
    .slice(0, 3);
}

export function serializeFilterState(state: FilterState): string {
  const params = new URLSearchParams();

  if (state.query.trim()) params.set("q", state.query.trim());

  const dimensions = (Object.entries(state.minScores) as Array<[DimensionKey, number]>)
    .filter(([, minimum]) => minimum > 0)
    .map(([key, minimum]) => `${key}:${minimum}`);
  if (dimensions.length) params.set("d", dimensions.join(","));
  if (state.travellerTypes.length) params.set("t", state.travellerTypes.join(","));
  if (state.maxCostLevel !== "any") params.set("cost", state.maxCostLevel);
  if (state.maxPriceSek !== null) params.set("price", String(state.maxPriceSek));
  if (state.maxDurationMinutes !== null) params.set("duration", String(state.maxDurationMinutes));
  if (state.maxTravelTimeMinutes !== null) params.set("travel", String(state.maxTravelTimeMinutes));
  if (state.minIndoorPercentage > 0) params.set("indoor", String(state.minIndoorPercentage));
  if (state.seasons.length) params.set("season", state.seasons.join(","));
  if (state.timesOfDay.length) params.set("time", state.timesOfDay.join(","));
  if (state.maxMobility !== "any") params.set("mobility", state.maxMobility);
  if (state.bookingPolicy !== "any") params.set("booking", state.bookingPolicy);
  if (state.transportModes.length) params.set("modes", state.transportModes.join(","));
  if (state.neighbourhoodCharacters.length) params.set("character", state.neighbourhoodCharacters.join(","));
  if (state.waterRelationships.length) params.set("water", state.waterRelationships.join(","));
  if (state.rainSafeOnly) params.set("rainSafe", "1");
  if (state.childrenOnly) params.set("children", "1");
  if (state.childAge !== null) params.set("age", String(state.childAge));
  if (state.carFreeOnly) params.set("carFree", "1");
  if (state.quietOnly) params.set("quiet", "1");
  if (state.toiletsOnly) params.set("toilets", "1");
  if (state.minStrollerScore > 0) params.set("stroller", String(state.minStrollerScore));
  if (state.maxPhysicalEffort !== null) params.set("effort", String(state.maxPhysicalEffort));
  if (state.sortBy !== "match") params.set("sort", state.sortBy);
  if (state.activePreset) params.set("preset", state.activePreset);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : window.location.pathname;
}

export function parseFilterState(search: string): FilterState {
  const state = createDefaultFilterState();
  const params = new URLSearchParams(search);

  state.query = params.get("q") ?? "";

  for (const pair of (params.get("d") ?? "").split(",").filter(Boolean)) {
    const [rawKey, rawMinimum] = pair.split(":");
    if (!rawKey || !rawMinimum) continue;
    const key = rawKey as DimensionKey;
    const minimum = Number(rawMinimum);
    if (key in state.minScores && Number.isInteger(minimum) && minimum >= 1 && minimum <= 5) {
      state.minScores[key] = minimum;
    }
  }

  state.travellerTypes = parseEnumList(
    params.get("t"),
    travellerDefinitions.map((definition) => definition.value),
  );

  const cost = params.get("cost") as CostLevel | null;
  if (cost && cost in costRanks) state.maxCostLevel = cost;

  state.maxPriceSek = parsePositiveOrZeroNumber(params.get("price"));
  state.maxDurationMinutes = parsePositiveNumber(params.get("duration"));
  state.maxTravelTimeMinutes = parsePositiveNumber(params.get("travel"));

  const indoor = Number(params.get("indoor"));
  if ([25, 50, 75, 100].includes(indoor)) state.minIndoorPercentage = indoor;

  state.seasons = parseEnumList(params.get("season"), seasonDefinitions.map((definition) => definition.value));
  state.timesOfDay = parseEnumList(params.get("time"), timeOfDayDefinitions.map((definition) => definition.value));

  const mobility = params.get("mobility") as MobilityLimit | null;
  if (mobility && mobilityLimitOptions.some((definition) => definition.value === mobility)) state.maxMobility = mobility;

  const booking = params.get("booking") as BookingPolicy | null;
  if (booking && bookingPolicyOptions.some((definition) => definition.value === booking)) state.bookingPolicy = booking;

  state.transportModes = parseEnumList(params.get("modes"), transportModeDefinitions.map((definition) => definition.value));
  state.neighbourhoodCharacters = parseEnumList(
    params.get("character"),
    neighbourhoodCharacterDefinitions.map((definition) => definition.value),
  );
  state.waterRelationships = parseEnumList(
    params.get("water"),
    waterRelationshipDefinitions.map((definition) => definition.value),
  );

  state.rainSafeOnly = params.get("rainSafe") === "1";
  state.childrenOnly = params.get("children") === "1";
  state.childAge = parseBoundedInteger(params.get("age"), 0, 17);
  state.carFreeOnly = params.get("carFree") === "1";
  state.quietOnly = params.get("quiet") === "1";
  state.toiletsOnly = params.get("toilets") === "1";
  state.minStrollerScore = parseScore(params.get("stroller")) ?? 0;
  state.maxPhysicalEffort = parseScore(params.get("effort"));

  const sort = params.get("sort") as SortOption | null;
  if (sort && ["match", "overall", "value", "duration", "price", "travel"].includes(sort)) {
    state.sortBy = sort;
  }

  state.activePreset = params.get("preset");
  return state;
}

function parseEnumList<T extends string>(value: string | null, validValues: T[]): T[] {
  const valid = new Set(validValues);
  return (value ?? "").split(",").filter((item): item is T => valid.has(item as T));
}

function parsePositiveNumber(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parsePositiveOrZeroNumber(value: string | null): number | null {
  if (value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function parseBoundedInteger(value: string | null, minimum: number, maximum: number): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= minimum && parsed <= maximum ? parsed : null;
}

function parseScore(value: string | null): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 5 ? parsed : null;
}

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} Std.` : `${hours} Std. ${rest} Min.`;
}

export function formatSek(value: number | undefined): string {
  if (value === undefined) return "offen";
  if (value === 0) return "kostenlos";
  return `${Math.round(value)} SEK`;
}
